import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Constants from 'style/constants';
import { calculateDurationInMinutes } from 'shared/utils/date-utils';

import TimelineItem from './types/timeline-item';

import './timeline.scss';

type Props = {
  prefix?: string;
  firstItem: TimelineItem;
  secondItem: TimelineItem;
  typographyVariant:
    | 'inherit'
    | 'button'
    | 'caption'
    | 'overline'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6';
  durationInMinutes: number;
  usePreCalculatedTime: boolean;
};

const Duration = ({
  prefix,
  firstItem,
  secondItem,
  typographyVariant,
  durationInMinutes,
  usePreCalculatedTime,
}: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('timeline');

  const getDurationTranslation = (): string => {
    const minutes = usePreCalculatedTime ? durationInMinutes : calculateDurationInMinutes(secondItem.createdOn, firstItem.createdOn, true);

    if (minutes >= 60) {
      let hours = minutes / 60;

      if (hours >= 24) {
        hours = Math.ceil(hours);

        const days = hours / 24;
        return `${t('duration_days', {
          count: Math.floor(days),
        })} ${t('and')} ${t('duration_hours', {
          count: hours % 24,
        })}`;
      }

      return `${t('duration_hours', {
        count: Math.floor(hours),
      })} ${t('and')} ${t('duration_minutes', {
        count: Math.ceil(minutes % 60),
      })}`;
    }

    return t('duration_minutes', {
      count: Math.ceil(minutes),
    });
  };

  return (
    <Typography variant={typographyVariant} sx={{ color: Constants.Colors.onSurface }}>
      {prefix && `${prefix}`} {getDurationTranslation()}
    </Typography>
  );
};

export default Duration;
